import React, { useEffect, useState, useCallback } from "react";
import Navbar from "./Navbar";
import Downloadapp from "./Downloadapp";
import Security from "./Security";
import Footer from "./Footer";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import secureLocalStorage from "./storage";
import { ToastContainer, toast } from "react-toastify";
import Manubar from "./Manubar";

const API_BASE = process.env.REACT_APP_API;

const CardList = () => {
  const navigate = useNavigate();

  // local values (ensure they exist)
  const _id = secureLocalStorage.getItem("n_id");
  const cart_list_id = secureLocalStorage.getItem("n_cartlist_id");
  const token = secureLocalStorage.getItem("n_token");
  const promocode = secureLocalStorage.getItem("n_promocode");
  const offerprice = secureLocalStorage.getItem("n_offerprice");

  // state
  const [show_cart_list, setshow_cart_list] = useState([]);
  const [totolprice, settotalprice] = useState(0);
  const [showdelivery, setshowdelivery] = useState(0);
  const [offer_price, setoffer_price] = useState(0);
  const [productQuantities, setProductQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [updatingItems, setUpdatingItems] = useState(new Set());
  const [deletingItems, setDeletingItems] = useState(new Set());

 secureLocalStorage.setItem("deliveryCharges",showdelivery);

  // axios instance helper that attaches token header for every request
  const axiosInstance = axios.create({
    baseURL: API_BASE,
    timeout: 20000,
  });
  axiosInstance.interceptors.request.use((cfg) => {
    if (token) cfg.headers = { ...(cfg.headers || {}), token };
    return cfg;
  });

  // totals
  const allprice = totolprice + showdelivery;
  const o_p = allprice >= (offerprice || 0) ? (offerprice || 0) : 0;
  const alltotal = totolprice + showdelivery - offer_price;

  // recalc totals whenever cart or quantities change
  useEffect(() => {
    const newTotal = show_cart_list.reduce((acc, item) => {
      const q = productQuantities[item.product_id] ?? item.no_of_product ?? 1;
      return acc + item.price * q;
    }, 0);
    settotalprice(newTotal);
  }, [show_cart_list, productQuantities]);

  // fetch cart details
  const fetchCartDetails = useCallback(async () => {
    // if auth missing, redirect home
    if (!_id == null || !token) {
      // don't immediately navigate if we just want an empty UI; but your original navigated to "/"
      navigate("/");
      return;
    }

    try {
      setLoading(true);
      const items = { user_id: _id };
      const res = await axiosInstance.post("website/cart_details", items);

      // assume server returns an array at res.data.data
      const data = res?.data?.data ?? [];
      setshow_cart_list(Array.isArray(data) ? data : []);
      // init quantities
      const initialQ = {};
      (Array.isArray(data) ? data : []).forEach((it) => {
        initialQ[it.product_id] = it.no_of_product ?? 1;
      });
      setProductQuantities(initialQ);
    } catch (err) {
      console.error("fetchCartDetails error:", err);
      toast.error("Failed to load cart items");
    } finally {
      setLoading(false);
    }
  }, [_id, token, navigate]);

  // initial load
  useEffect(() => {
    fetchCartDetails();
  }, [fetchCartDetails]);

  // fetch delivery charge
  useEffect(() => {
    const fetchDeliveryCharge = async () => {
      if (!token) return;
      try {
        const res = await axiosInstance.get("/website/delivery_charge_list");
        const charge = res?.data?.data?.delivery_charge;
        if (typeof charge === "number") setshowdelivery(charge);
      } catch (error) {
        console.error("delivery charge error:", error);
      }
    };
    fetchDeliveryCharge();
  }, [token]); // axiosInstance uses token via interceptor

  // navigation to single product
  const Single_products = (singleproducts) => {
    console.log("singleproducts",singleproducts)
    secureLocalStorage.setItem("n_singleproducts", singleproducts);
    navigate(`/single_products/${singleproducts}`);
  };

  // delete from cart: call API -> on success refresh or update local state consistently
  const delete_card = async (productId) => {
    if (!_id || !token) {
      toast.error("Authentication required");
      return;
    }

    // prevent double deletion
    if (deletingItems.has(productId)) return;

    setDeletingItems((prev) => new Set(prev).add(productId));

    try {
      const payload = {
        user_id: _id,
        product_id: productId,
        quantity: "0",
      };

      const res = await axiosInstance.post("website/delete_cart_product", payload);

      // check server result; adapt to your server shape (res.data.result || res.data.success)
      if (res?.data?.result || res?.data?.success) {
        // Option A: update local state (faster UX)
        setshow_cart_list((prev) => {
          const updated = prev.filter((it) => it.product_id !== productId);
          // if empty after deletion, optionally redirect
          if (updated.length === 0) {
            // short delay so user sees toast
            setTimeout(() => navigate("/"), 700);
          }
          return updated;
        });

        // remove quantity entry
        setProductQuantities((prev) => {
          const copy = { ...prev };
          delete copy[productId];
          return copy;
        });

        toast.success("Item removed from cart");
        // Also fetch server state to ensure consistency
        setTimeout(fetchCartDetails, 600); // small delay to let DB settle
      } else {
        console.warn("delete_card: server returned failure:", res?.data);
        toast.error(res?.data?.message || "Failed to remove item");
        // keep UI in sync
        fetchCartDetails();
      }
    } catch (err) {
      console.error("delete_card error:", err);
      toast.error("Failed to remove item");
      fetchCartDetails();
    } finally {
      setDeletingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  // increment / decrement quantity (local only until update API called)
  const increment = (productId) => {
    setProductQuantities((prev) => ({
      ...prev,
      [productId]: (prev[productId] ?? 1) + 1,
    }));
  };

  const decrement = (productId) => {
    setProductQuantities((prev) => {
      const cur = prev[productId] ?? 1;
      if (cur <= 1) return prev;
      return { ...prev, [productId]: cur - 1 };
    });
  };

  // update cart item (quantity) with server
  const updatecard = async (item) => {
    if (!_id || !token) return;

    // prevent double update
    if (updatingItems.has(item.product_id)) return;

    setUpdatingItems((prev) => new Set(prev).add(item.product_id));

    try {
      const currentQuantity = productQuantities[item.product_id] ?? item.no_of_product ?? 1;

      const data = {
        user_id: _id,
        product_id: item.product_id,
        quantity: currentQuantity.toString(),
        no_of_product: currentQuantity,
        price: item.price,
        mrp: item.mrp,
      };

      const res = await axiosInstance.post("website/update_to_cart", data);

      if (res?.data?.result || res?.data?.success) {
        // Fast local update: reflect new no_of_product
        setshow_cart_list((prev) =>
          prev.map((c) =>
            c.product_id === item.product_id ? { ...c, no_of_product: currentQuantity } : c
          )
        );
        toast.success("Cart updated successfully");
        // sync to be safe
        setTimeout(fetchCartDetails, 500);
      } else {
        console.warn("updatecard: server returned failure:", res?.data);
        toast.error(res?.data?.message || "Failed to update cart");
        fetchCartDetails(); // revert local changes by re-fetch
      }
    } catch (err) {
      console.error("updatecard error:", err);
      toast.error("Failed to update cart");
      fetchCartDetails();
    } finally {
      setUpdatingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(item.product_id);
        return newSet;
      });
    }
  };

  // promo code
  const applycode = () => {
    if (!offerprice) {
      toast.warning("No promo code available");
      return;
    }
    setoffer_price(o_p);
    secureLocalStorage.removeItem("n_promocode");
    secureLocalStorage.removeItem("n_offerprice");
    secureLocalStorage.setItem("n_oofferprice_chackout", offerprice);
    toast.success("Promo code applied successfully");
  };

  // checkout (kept as-is but simplified error handling)
  const checkout = () => {
    if (show_cart_list.length === 0) {
      toast.warning("Your cart is empty");
      return;
    }

    setIsCheckoutLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await axios.get(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${position.coords.latitude},${position.coords.longitude}&key=AIzaSyAibWV0NM3Z51ZB0qIeKfxBBFs3vYZxUuM`
          );
          const results = response.data.results;
          const locationdata = {
            user_id: _id,
            landmark: results?.[0]?.address_components?.[1]?.long_name,
            address: results?.[0]?.formatted_address,
            city: results?.[0]?.address_components?.[3]?.long_name,
            pin_code: Number(results?.[0]?.address_components?.[7]?.long_name),
          };

          const res = await axiosInstance.post("website/add_address", locationdata);
          if (res?.data?.result) {
            navigate(`/checkout`);
          } else {
            toast.error("Failed to save address, proceeding anyway");
            navigate(`/checkout`);
          }
        } catch (error) {
          console.error("Error saving address:", error);
          navigate(`/checkout`);
        } finally {
          setIsCheckoutLoading(false);
        }
      },
      (error) => {
        console.warn("Geolocation failed:", error);
        navigate(`/checkout`);
        setIsCheckoutLoading(false);
      }
    );
  };

  const calculateItemSubtotal = (item) => {
    const quantity = productQuantities[item.product_id] ?? item.no_of_product ?? 1;
    return (item.price * quantity).toFixed(2);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div>
        <ToastContainer position="top-center" />
        <div className="modal fade offcanvas-modal" id="exampleModal">
          <div className="modal-dialog offcanvas-dialog">
            <div className="modal-content">
              <Manubar />
            </div>
          </div>
        </div>

        <header>
          <Navbar />
        </header>

        <nav className="breadcrumb-section">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <ol className="breadcrumb bg-transparent m-0 p-0 align-items-center">
                  <li className="breadcrumb-item">
                    <Link to="/">Home</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Cart Page
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </nav>

        <section className="whish-list-section section-mb">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <h3 className="title mb-5 pb-3 text-capitalize">Your Cart Items</h3>

                {show_cart_list.length === 0 ? (
                  <div className="text-center py-5">
                    <h4>Your cart is empty</h4>
                    <Link to="/" className="btn btn-dark mt-3">
                      Continue Shopping
                    </Link>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table">
                      <thead className="thead-light">
                        <tr>
                          <th className="text-center">PRODUCT IMAGE</th>
                          <th className="text-center">PRODUCT NAME</th>
                          <th className="text-center">PRICE</th>
                          <th className="text-center">QUANTITY</th>
                          <th className="text-center">ACTION</th>
                          <th className="text-center">SUBTOTAL</th>
                          {/* <th className="text-center">UPDATE</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {show_cart_list.map((item, index) => {
                          const currentQuantity = productQuantities[item.product_id] ?? item.no_of_product ?? 1;
                          const subtotal = calculateItemSubtotal(item);
                          const isUpdating = updatingItems.has(item.product_id);
                          const isDeleting = deletingItems.has(item.product_id);

                          return (
                            <tr key={`${item.product_id}-${index}`}>
                              <th className="text-center" scope="row">
                                <img
                                  src={`${API_BASE}uploads/${item.product_image}`}
                                  width={"100px"}
                                  height={"100px"}
                                  alt={item.product_name}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => Single_products(item.product_id)}
                                />
                              </th>
                              <td className="text-center">
                                <span className="whish-title">{item.product_name}</span>
                              </td>
                              <td className="text-center">
                                <span className="whish-list-price">₹{item.price}</span>
                              </td>
                              <td className="text-center">
                                <div className="product-count style">
                                  <div className="count d-flex justify-content-center align-items-center">
                                    <div className="button-group d-flex align-items-center">
                                      <button
                                        className="count-btn decrement btn btn-outline-secondary"
                                        onClick={() => decrement(item.product_id)}
                                        disabled={currentQuantity <= 1 || isUpdating || isDeleting}
                                      >
                                        <span className="las la-minus" />
                                      </button>

                                      <span className="mx-3" style={{ minWidth: "30px" }}>
                                        {currentQuantity}
                                      </span>

                                      <button
                                        className="count-btn increment btn btn-outline-secondary"
                                        onClick={() => increment(item.product_id)}
                                        disabled={isUpdating || isDeleting}
                                      >
                                        <span className="las la-plus" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="text-center">
                                <span
                                  style={{ cursor: isDeleting ? "not-allowed" : "pointer" }}
                                  className="trash"
                                  onClick={() => !isDeleting && delete_card(item.product_id)}
                                  title={isDeleting ? "Removing..." : "Remove item"}
                                >
                                  {isDeleting ? (
                                    <div className="spinner-border spinner-border-sm text-danger" role="status">
                                      <span className="visually-hidden">Removing...</span>
                                    </div>
                                  ) : (
                                    <i className="ion-android-delete" style={{ color: "red", fontSize: "25px" }} />
                                  )}
                                </span>
                              </td>
                              <td className="text-center">
                                <span className="whish-list-price">₹{subtotal}</span>
                              </td>
                              {/* <td className="text-center">
                                <button
                                  className="btn btn-dark btn-sm"
                                  onClick={() => updatecard(item)}
                                  disabled={isUpdating || isDeleting}
                                >
                                  {isUpdating ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" />
                                      Updating...
                                    </>
                                  ) : (
                                    "Update"
                                  )}
                                </button>
                              </td> */}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {show_cart_list.length > 0 && (
          <div className="check-out-section section-mb">
            <div className="container">
              <div className="row">
                {/* <div className="col-lg-7">
                  <div className="billing-info-wrap">
                    <h3 className="title" style={{ color: "green" }}>
                      <Link to="/offer">Check Offers</Link>
                    </h3>
                    <form className="personal-information">
                      <div className="row">
                        <div className="col-12">
                          <h3 className="coupon-title">Discount Coupon Code</h3>
                        </div>
                        <div className="col-lg-6 col-md-6">
                          <div className="billing-info">
                            <input className="form-control" value={promocode || ""} placeholder="Coupon Code" type="text" readOnly />
                          </div>
                        </div>
                        <div className="col-lg-6 col-md-6">
                          <button type="button" className="btn btn-dark check-out-btn" onClick={applycode} disabled={!promocode}>
                            Apply Code
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div> */}
                <div className="col-lg-5 mt-4 mt-lg-0">
                  <div className="your-order-area">
                    <div className="your-order-wrap gray-bg-4">
                      <div className="your-order-product-info">
                        <div className="your-order-top">
                          <ul>
                            <li>Subtotal</li>
                            <li>₹{totolprice.toFixed(2)}</li>
                          </ul>
                          <ul>
                            <li>Delivery Charges</li>
                            <li>₹{showdelivery.toFixed(2)}</li>
                          </ul>
                          {offer_price > 0 && (
                            <ul>
                              <li>Discount</li>
                              <li>-₹{offer_price.toFixed(2)}</li>
                            </ul>
                          )}
                        </div>
                        <div className="your-order-total mb-0">
                          <ul>
                            <li className="order-total">Total</li>
                            <li>₹{alltotal.toFixed(2)}</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="Place-order mt-5">
                      <button onClick={checkout} className="btn btn-dark btn-lg w-100" disabled={isCheckoutLoading}>
                        {isCheckoutLoading ? "Please wait..." : "Proceed to Checkout"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <Security />
        <Footer />
      </div>
    </>
  );
};

export default CardList;
